import { TWidget } from 'widgets/Widget';
type TInputDateNumeric = ReturnType<typeof import('../forms/InputDateNumeric').default>;

/**
 * @description Profile Form
 * @param Widget base widget for extending
 * @returns DateNumericPicker class
 */

function DateNumericPickerClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory forms
     * @class DateNumericPicker
     * @augments Widget
     * @classdesc DateNumericPicker extends base widget
     */
    class DateNumericPicker extends Widget {
        day = '';

        month = '';

        year = '';

        prefs() {
            return {
                errorRef: 'dateError',
                forbidFuture: false,
                ...super.prefs()
            };
        }

        toggleError(show: boolean, invalidateAll = false) {
            this.ref(this.prefs().errorRef).toggle(show);

            if (show && invalidateAll) {
                const InputDateNumeric = <TInputDateNumeric>this.getConstructor('inputDateNumeric');

                this.items.forEach(item => {
                    if (item instanceof InputDateNumeric) {
                        item.setError('Invalid date');
                    }
                });
            }
        }

        /**
         * @description If some of the date numeric fields are filled, highlight the empty field otherwise, remove the highlight
         * @param item date numeric field to toggle highlight
         */
        toggleEmptyFieldHighlight(item) {
            const InputDateNumeric = <TInputDateNumeric>this.getConstructor('inputDateNumeric');
            const hasAnyDatePart = this.day || this.month || this.year;

            if (item instanceof InputDateNumeric) {
                const isEmpty = !item.getValue();

                item.toggleErrorState(Boolean(hasAnyDatePart && isEmpty));
            }
        }

        // eslint-disable-next-line sonarjs/cognitive-complexity
        handleInputChange() {
            const InputDateNumeric = <TInputDateNumeric>this.getConstructor('inputDateNumeric');
            let hasError = false;

            this.items.forEach(item => {
                if (item instanceof InputDateNumeric) {
                    if (/^.*date$/.test(item.id ?? '')) {
                        this.day = item.getValue();
                    }

                    if (/^.*month$/.test(item.id ?? '')) {
                        this.month = item.getValue();
                    }

                    if (/^.*year$/.test(item.id ?? '')) {
                        this.year = item.getValue();
                    }

                    if (item.getValue()) {
                        item.validate();
                    }

                    if (!item.isValid()) {
                        hasError = true;
                    }
                }
            });

            this.toggleError(hasError);

            if (!hasError && this.day && this.month && this.year) {
                const date = new Date(`${this.year}-${this.month}-${this.day}`);
                const isFuture = this.prefs().forbidFuture && date > new Date();

                this.toggleError(date.toString() === 'Invalid Date' || date.getDate() !== Number(this.day), true);

                if (isFuture) {
                    const yearField = this.items.find(item => /^.*year$/.test(item.id ?? ''));

                    if (yearField instanceof InputDateNumeric) {
                        yearField.setError('Invalid date');
                    }

                    this.toggleError(true);
                }
            }
        }

        /**
         * @description Highlights the date numeric fields and set the general error message
         * @listens InputDateNumeric#togglegeneralerrorstate
         */
        toggleGeneralErrorState() {
            const InputDateNumeric = <TInputDateNumeric>this.getConstructor('inputDateNumeric');

            this.items.forEach(item => {
                if (item instanceof InputDateNumeric) {
                    item.toggleErrorState(true);
                }
            });

            this.toggleError(true);
        }
    }

    return DateNumericPicker;
}

export type TDateNumericPicker = ReturnType<typeof DateNumericPickerClassCreator>;

export type TDateNumericPickerInstance = InstanceType<TDateNumericPicker>;

export default DateNumericPickerClassCreator;
